import { BLOG_DIR } from "./env";
import type { Metadata } from "../types/sanity.types";

const rootPath = `/${BLOG_DIR}`;

const blogPaths = {
  root: rootPath,
  post: `${rootPath}/post`,
  category: `${rootPath}/category`,
  tag: `${rootPath}/tag`,
  author: `${rootPath}/author`,
  sitemap: `${rootPath}/sitemap.xml`,
  search: `${rootPath}/search`
};

export type BlogContentItem = {
  metadata: Pick<Metadata, "slug">;
};
export function getPostPath(
  clusterSlug: string,
  categorySlug: string,
  postSlug: string
): string {
  return `${blogPaths.root}/${clusterSlug}/${categorySlug}/${postSlug}`;
}
export function getVideoPath(
  clusterSlug: string,
  categorySlug: string,
  videoSlug: string
): string {
  return `${blogPaths.root}/${clusterSlug}/${categorySlug}/${videoSlug}`;
}
export function getCategoryPath(
  clusterSlug: string | undefined,
  categorySlug: string | undefined
): string {
  return `${blogPaths.root}/${clusterSlug}/${categorySlug}`;
}
export function getClusterPath(clusterSlug: string | undefined): string {
  return `${blogPaths.root}/${clusterSlug}`;
}
export function getTagPath(tagSlug: string): string {
  return `${blogPaths.tag}/${tagSlug}`;
}

export function getAuthorsPath(): string {
  return `${blogPaths.author}`;
}

export function getAuthorPath(slug: string): string {
  return `${blogPaths.author}/${slug}`;
}

export function getBlogRootPath(): string {
  return blogPaths.root;
}

export function getPagePath(slug: string): string {
  return `${blogPaths.root}/${slug}`;
}

export function getSitemapPath(): string {
  return blogPaths.sitemap;
}

export function getSearchPath(): string {
  return blogPaths.search;
}

export function getHomePath(): string {
  return blogPaths.root;
}

export function isValidBlogPath(path: string): boolean {
  return path.startsWith(rootPath);
}

export function getBreadcrumbs(
  currentPath: string
): Array<{ label: string; href: string }> {
  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: "Blog", href: blogPaths.root }
  ];

  if (!isValidBlogPath(currentPath) || currentPath === blogPaths.root) {
    return breadcrumbs;
  }

  const pathSegments = currentPath
    .replace(rootPath, "")
    .split("/")
    .filter(Boolean);

  // Check if it's a special path like /blog/tag/*, /blog/author/*, etc.
  if (pathSegments.length === 2) {
    const [firstSegment] = pathSegments;

    switch (firstSegment) {
      case "category":
      case "tag":
      case "author": {
        const label =
          firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
        breadcrumbs.push({ label, href: currentPath });
        return breadcrumbs;
      }
    }

    // Otherwise it's /blog/[clusterSlug]/[categorySlug]
    const [clusterSlug, categorySlug] = pathSegments;
    breadcrumbs.push({
      label: "Category",
      href: `${blogPaths.root}/${clusterSlug}/${categorySlug}`
    });
  } else if (pathSegments.length === 3) {
    const [clusterSlug, categorySlug] = pathSegments;
    // Formato: /blog/[clusterSlug]/[categorySlug]/[postSlug]
    breadcrumbs.push({
      label: "Category",
      href: `${blogPaths.root}/${clusterSlug}/${categorySlug}`
    });
    breadcrumbs.push({ label: "Post", href: currentPath });
  }

  return breadcrumbs;
}

export function getCanonicalUrl(path: string, baseUrl?: string): string {
  const base =
    baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${path}`;
}

export const basePath = (type: keyof typeof blogPaths) => {
  return blogPaths[type];
};
